import React, { useCallback, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography
} from '@mui/joy';

import ipAddressSchema from '../../schema/ipAddressSchema';
import { WindscreenService } from '../../services/WindscreenService';

interface IpAddressForm {
  ipAddress: string;
}

const WindscreenManagement: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [currentIp, setCurrentIp] = useState<string>('');
  const [serviceStatus, setServiceStatus] = useState<string>('');
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [newIpAddress, setNewIpAddress] = useState<string>('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues
  } = useForm<IpAddressForm>({
    defaultValues: {
      ipAddress: ''
    },
    resolver: yupResolver(ipAddressSchema(t))
  });

  // Fetch windscreen status
  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const response = await WindscreenService.getStatus();
      setCurrentIp(response.data.current_ip);
      setServiceStatus(response.data.service_status);
      reset({ ipAddress: response.data.current_ip });
    } catch (error) {
      toast.error(t('windscreen.errors.fetch_status_failed'));
      console.error('Failed to fetch windscreen status:', error);
    } finally {
      setLoading(false);
      setStatusLoading(false);
    }
  }, [setStatusLoading, setCurrentIp, setServiceStatus, reset, t, setLoading]);

  // Fetch initial status
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Handle form submission
  const onSubmit: SubmitHandler<IpAddressForm> = useCallback((data) => {
    setNewIpAddress(data.ipAddress);
    setConfirmModalOpen(true);
  }, [setNewIpAddress, setConfirmModalOpen]);

  // Update IP address
  const updateIpAddress = useCallback(async () => {
    try {
      setUpdateLoading(true);
      setConfirmModalOpen(false);

      await WindscreenService.updateIpAddress(newIpAddress);

      toast.success(t('windscreen.messages.ip_updated_success'));

      // Refresh status after successful update
      await fetchStatus();
    } catch (error: any) {
      if (error.status === 400) {
        toast.error(t('windscreen.errors.invalid_ip'));
      } else {
        toast.error(t('windscreen.errors.update_failed'));
      }
      console.error('Failed to update IP address:', error);
    } finally {
      setUpdateLoading(false);
    }
  }, [fetchStatus, newIpAddress, setConfirmModalOpen, setUpdateLoading, t]);

  return (
    <Box className="p-4">
      <Card variant="outlined" className="w-full max-w-3xl mx-auto">
        <CardContent>
          <Typography level="h4" className="mb-4">
            {t('windscreen.title')}
          </Typography>

          {loading ? (
            <Box className="flex justify-center items-center py-8">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Status Section */}
              <Box className="mb-6">
                <Typography level="title-md" className="mb-2">
                  {t('windscreen.current_status')}
                </Typography>

                <Box className="flex flex-col md:flex-row gap-4 mt-4">
                  <Card variant="soft" className="flex-1">
                    <CardContent>
                      <Box className="flex items-center justify-between">
                        <Typography level="title-sm">{t('windscreen.current_ip')}</Typography>
                        {statusLoading ? (
                          <CircularProgress size="sm" />
                        ) : (
                          <Typography level="body-md">{currentIp}</Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  <Card
                    variant="soft"
                    color={serviceStatus === 'running' ? 'success' : 'danger'}
                    className="flex-1"
                  >
                    <CardContent>
                      <Box className="flex items-center justify-between">
                        <Typography level="title-sm">{t('windscreen.service_status')}</Typography>
                        {statusLoading ? (
                          <CircularProgress size="sm" />
                        ) : (
                          <Box className="flex items-center">
                            {serviceStatus === 'running' ? (
                              <>
                                <CheckCircleIcon className="mr-1" fontSize="small" />
                                <Typography level="body-md">{t('windscreen.status.active')}</Typography>
                              </>
                            ) : (
                              <>
                                <ErrorIcon className="mr-1" fontSize="small" />
                                <Typography level="body-md">{t('windscreen.status.inactive')}</Typography>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                <Box className="flex justify-end mt-2">
                  <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<RefreshIcon />}
                    onClick={fetchStatus}
                    loading={statusLoading}
                    disabled={statusLoading}
                    className="mt-2"
                  >
                    {t('windscreen.refresh')}
                  </Button>
                </Box>
              </Box>

              <Divider className="my-4" />

              {/* Update IP Form */}
              <Box className="mt-6">
                <Typography level="title-md" className="mb-4">
                  {t('windscreen.update_ip')}
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <Controller
                    control={control}
                    name="ipAddress"
                    render={({ field }) => (
                      <FormControl error={!!errors.ipAddress}>
                        <FormLabel>{t('windscreen.ip_address')}</FormLabel>
                        <Input
                          {...field}
                          placeholder="192.168.1.100"
                          disabled={updateLoading}
                        />
                        {errors.ipAddress && (
                          <FormHelperText>{errors.ipAddress.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />

                  <Box className="flex justify-end mt-4">
                    <Button
                      type="submit"
                      loading={updateLoading}
                      disabled={updateLoading}
                    >
                      {t('windscreen.update')}
                    </Button>
                  </Box>
                </form>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          aria-labelledby="confirm-modal-title"
          aria-describedby="confirm-modal-description"
        >
          <ModalClose />
          <Typography id="confirm-modal-title" level="h2">
            {t('windscreen.confirm_update.title')}
          </Typography>
          <Typography id="confirm-modal-description" textColor="text.tertiary">
            {t('windscreen.confirm_update.message', { ipAddress: newIpAddress })}
          </Typography>
          <Stack direction="row" spacing={1} className="mt-4 justify-end">
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setConfirmModalOpen(false)}
            >
              {t('windscreen.confirm_update.cancel')}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={updateIpAddress}
            >
              {t('windscreen.confirm_update.confirm')}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default WindscreenManagement;
